import { ReactElement, useMemo } from 'react';
import { Layer, Marker, Source } from 'react-map-gl/mapbox';
import styles from './beacon-map-marker.module.scss';
import { BEACON_ZINDEX } from '../util/map-helpers';
import { BeaconSnapshot } from '@/api';
import circle from '@turf/circle';
import { blue } from '@/ui/styles';
import { uuidv4 } from '@/common/util';
import { useTasksTranslation } from '../hooks';

const imgUrl =
    'https://safepoint-user-logos-public.s3.eu-west-1.amazonaws.com/beacons/beacon.png';

export type BeaconMapMarkerProps = {
    lat: number;
    lng: number;
    distanceMeters: number;
    isSelected?: boolean;
    beacon: BeaconSnapshot;
    updatedAt: Date;
    opacity?: number;
    onClick?: () => void;
};

export function BeaconMapMarker(props: BeaconMapMarkerProps): ReactElement {
    const { t } = useTasksTranslation();
    const {
        lat,
        lng,
        distanceMeters,
        isSelected = false,
        opacity,
        onClick
    } = props;

    const radiusCircleData = useMemo(() => {
        if (!isSelected) {
            return null;
        }
        return circle([lng, lat], distanceMeters, {
            steps: 100,
            units: 'meters'
        });
    }, [lat, lng, distanceMeters, isSelected]);

    const radiusSourceId = useMemo(() => {
        return uuidv4();
    }, []);

    return (
        <>
            <Marker
                longitude={lng}
                latitude={lat}
                anchor={'bottom'}
                style={{ zIndex: BEACON_ZINDEX }}
                onClick={(e) => {
                    e.originalEvent.stopPropagation();
                    onClick?.();
                }}
            >
                <div className={styles.markerContainer} style={{ opacity }}>
                    <div className={styles.dummyContainer} />
                    <div className={styles.imageContainer}>
                        <img
                            src={imgUrl}
                            className={styles.image}
                            alt={t('shared.tasks.beacon')}
                        />
                    </div>
                    <div className={styles.arrow} />
                </div>
            </Marker>
            {radiusCircleData && (
                <Source
                    id={`circleSource-${radiusSourceId}`}
                    type="geojson"
                    data={radiusCircleData}
                >
                    <Layer
                        id={`circleLayer-${radiusSourceId}`}
                        type="fill"
                        paint={{
                            'fill-color': blue,
                            'fill-opacity': 0.3
                        }}
                    />
                </Source>
            )}
        </>
    );
}
